import { useNavigate } from "react-router-dom";
import images from "../../assets/images";
import TabsContentHeader from "../../components/AdminDashboardTabsContentHeader/TabsContentHeader";
import "./AdminBulkUpload.scss";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import Select, { StylesConfig } from "react-select";
import { getScreenDimensions } from "../../utils/helper";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { GET_SUBJECTS } from "../TeacherStudentClassroom/api/TeacherStudentClassroomQuery";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import { visibility } from "../../redux/slices/rightSideAd";
import {
  GET_CLASSROOMS_BY_SCHOOL,
  GET_CLASSROOMS_BY_SUBJECT_AND_SCHOOL,
} from "../AdminClassroom/api/AdminClassroomQuery";
import ClassroomSelector from "./components/ClassroomSelector";
import BulkUpload from "./components/BulkUpload";
import {
  Autocomplete,
  CircularProgress,
  createFilterOptions,
  TextField,
} from "@mui/material";
import { categoryType } from "../../components/Popups/FileUpload";
import { messages, patterns } from "../../utils/constants";
import {
  ADD_CATEGORY,
  GET_CATEGORY,
} from "../SyndicatedVideos/api/SyndicatedVideosQuery";
import ToasterWithMessage from "../../components/toaster";

type SubjectOption = {
  value: string;
  label: string;
};

export type PathOption = {
  value: AllowedPath;
  label: AllowedPath;
};

type Classroom = {
  cId: string;
  cName: string;
  cOriginalName: string;
  cCode: string;
  cStudentsCount: number;
  deletedAt: string | null;
};

export type AllowedPath =
  | "Images"
  | "Podcast"
  | "Syndicated Videos"
  | "E-books (Learning Materials)"
  | "E-books (Question Bank)"
  | "E-books (Examination Guide)";

const AdminBulkUpload = () => {
  const { bookIcon, backArrow } = images;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  dispatch(visibility({ isVisible: true }));
  const { error } = ToasterWithMessage();

  const [subjectOptions, setSubjectOptions] = useState<SubjectOption[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<SubjectOption | null>(
    null
  );
  const [selectedPath, setSelectedPath] = useState<PathOption | null>(null);
  const [pathoptions, setPathOptions] = useState<PathOption[]>([]);
  const [allClasses, setAllClasses] = useState<Classroom[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<Classroom[]>([]);
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [categories, setCategories] = useState<categoryType[]>([]);
  const [videoCategoryError, setVideoCategoryError] = useState<string | null>(
    null
  );
  const [videoCategoryValue, setVideoCategoryValue] =
    useState<categoryType | null>();
  const [videoCategoryData, setVideoCategoryData] = useState<
    categoryType | null | undefined
  >();
  const [inputValue, setInputValue] = useState<string>("");
  const filter = createFilterOptions<categoryType>();
  const [descriptionValue, setDescriptionValue] = useState<string>("");
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null); // Add ref for textarea
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [authorNameValue, setAuthorNameValue] = useState<string>("");
  const [authorNameError, setAuthorNameError] = useState<string | null>(null);

  const { isMobileScreen } = getScreenDimensions();
  const profile = useAppSelector((state) => state?.auth?.adminProfile);
  const schoolAdminId =
    profile?.getCurrentUser?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saId;

  const customStyle: StylesConfig<SubjectOption, false> = {
    control: (styles) => ({
      ...styles,
      cursor: "pointer",
      borderRadius: "10px",
      width: isMobileScreen ? "100%" : "100%",
      height: isMobileScreen ? "41px" : "100%",
      padding: "0px",
      minHeight: isMobileScreen ? "41px" : "100%",
      border: "none",
      backgroundColor: isMobileScreen ? "#F0F3F5" : "#F0F3F5",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    indicatorsContainer: (styles) => ({
      ...styles,
      padding: "0px 8px",
    }),
    input: (styles) => ({
      ...styles,
      fontFamily: "Inter",
      fontWeight: "400",
      fontSize: "12px",
    }),
    placeholder: (styles) => ({
      ...styles,
      fontFamily: "Inter",
      fontWeight: "400",
      fontSize: "12px",
      color: "#B1B1B1",
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isFocused
        ? "#f5f5f5"
        : isSelected
        ? "#e6e6e6"
        : undefined,
      color: "#333",
      fontFamily: "Inter",
      fontWeight: isSelected ? "600" : "400",
      fontSize: "12px",
      cursor: "pointer",
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "#333",
      fontFamily: "Inter",
      fontWeight: "400",
      fontSize: "12px",
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      color: "#2D2D2D",
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: "200px",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "180px",
      overflowY: "auto",
    }),
  };

  const sxStyles = {
    ".MuiOutlinedInput-root": {
      paddingRight: "45px !important",
      fontWeight: 500,
      fontFamily: "var(--font-inter)",
      fontSize: "var(--font-size-xs)",
      backgroundColor: "var(--color-whitesmoke-400)",
      borderRadius: "var(--br-3xs)",
      height: 40,
      flexDirection: "row",
      alignItems: "center",
      width: "100%",
    },
    ".MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, .MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root":
      {
        paddingRight: 0, // Set padding-right to 0 for specified classes
      },
    ".MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot, .MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot":
      {
        paddingRight: 0, // Set padding-right to 0 for specified classes
      },
    ".MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    ".MuiFormLabel-root": {
      fontWeight: 500,
      fontFamily: "var(--font-inter)",
      fontSize: "var(--font-size-xs)",
      display: "none",
    },
    ".MuiInputLabel-root": {
      fontWeight: 500,
      fontFamily: "var(--font-inter)",
      fontSize: "var(--font-size-xs)",
      display: "none",
    },
    width: "100%",
    Padding: "0px",
    ".MuiAutocomplete-popupIndicator": {
      display: "none",
    },
  };

  const { data: subjectData, loading } = useQuery(GET_SUBJECTS, {
    fetchPolicy: "network-only",
  });

  const { data: allClassroom, loading: allClassroomLoading } = useQuery(
    GET_CLASSROOMS_BY_SCHOOL,
    {
      variables: { schoolAdminId },
      fetchPolicy: "network-only",
    }
  );

  const [getclassroomBysubjectId] = useLazyQuery(
    GET_CLASSROOMS_BY_SUBJECT_AND_SCHOOL,
    {
      fetchPolicy: "network-only",
      onCompleted(data) {
        setSelectedClasses(data.allClassrooms.nodes);
      },
    }
  );

  useEffect(() => {
    if (subjectData?.getConfiguredSubjects) {
      const options = subjectData.getConfiguredSubjects.map((subject: any) => ({
        value: subject.sjId,
        label: subject.sjOriginalName,
      }));
      setSubjectOptions(options);
    }
  }, [subjectData]);

  useEffect(() => {
    if (allClassroom?.allClassrooms?.nodes) {
      setAllClasses(allClassroom.allClassrooms.nodes);
    }
  }, [allClassroom]);

  useEffect(() => {
    const pathOptions: PathOption[] = [
      {
        value: "E-books (Learning Materials)",
        label: "E-books (Learning Materials)",
      },
      {
        value: "E-books (Question Bank)",
        label: "E-books (Question Bank)",
      },
      {
        value: "E-books (Examination Guide)",
        label: "E-books (Examination Guide)",
      },
      {
        value: "Images",
        label: "Images",
      },
      {
        value: "Podcast",
        label: "Podcast",
      },
      {
        value: "Syndicated Videos",
        label: "Syndicated Videos",
      },
    ];
    setPathOptions(pathOptions);
  }, []);

  useEffect(() => {
    if (selectedPath && selectedSubject) {
      getclassroomBysubjectId({
        variables: {
          subjectId: selectedSubject.value,
          schoolAdminId: schoolAdminId,
        },
      });
    }
    setDescriptionError("");
    setVideoCategoryError("");
    setVideoCategoryData(null);
    setDescriptionValue("");
    setAuthorNameError("")
    setAuthorNameValue("")
  }, [selectedPath, selectedSubject]);

  const validateVideoCategory = (value: string | null | undefined) => {
    if (!value || value === "") {
      return "Video category is required";
    }
    if (value.length < 1 || value.length > 100) {
      return "Video category must be between 1 and 100 characters long";
    } else {
      if (!patterns.alphabetAndNumber.test(value)) {
        return messages.VideoCategory;
      }
      if (!patterns.NotStartWithNumber.test(value)) {
        return messages.VideoCategoryNotStartNumber;
      }
    }
    return null;
  };

  const [getCategory, { loading: getCategoryLoading }] = useLazyQuery(
    GET_CATEGORY,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const fetchedCategories = data.allSubjectCategories.nodes.map(
          (videoData: any) => ({
            label: videoData.scNameCapitalized,
            id: videoData.scId,
          })
        );
        setCategories(fetchedCategories);
      },
      onError: (err) => {
        error(err?.message);
      },
    }
  );

  const [addCategory, { loading: addCategoryLoading }] = useMutation(
    ADD_CATEGORY,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data?.addSubjectCategory) {
          setVideoCategoryValue((prevValue) => {
            if (prevValue) {
              return {
                ...prevValue,
                id: data.addSubjectCategory,
                label: prevValue.label, // Keep existing label or modify as needed
              };
            } else {
              return {
                id: data.addSubjectCategory,
                label: "", // Default label if prevValue is null or undefined
              };
            }
          });
          getCategory({
            variables: {
              sjId: selectedSubject?.value,
            },
          });
        }
      },
      onError: (err) => {
        error(err?.message);
      },
    }
  );

  useEffect(() => {
    if (selectedSubject) {
      getCategory({
        variables: {
          sjId: selectedSubject?.value,
        },
      });
    }
  }, [selectedSubject]);

  const validateDescription = (value: string) => {
    if (!value || value === "") {
      return "Description is required";
    }
    if (!patterns.descriptionLengthRegex.test(value)) {
      return messages.descriptionLength;
    }
    return null;
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDescriptionValue(value);
    setDescriptionError("");
    const validationError: any = validateDescription(value.trim());
    setDescriptionError(validationError);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Enter" &&
        document.activeElement !== descriptionRef.current
      ) {
        e.preventDefault(); // Prevent the default form submission
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [descriptionValue, videoCategoryValue]); // Ensure the latest fileName and file are used

  console.log(hasSubmitted, descriptionError, "allclasses");

  const ErrorInFields = descriptionError || videoCategoryError || authorNameError;

  //To validate file name
  const validateFileName = (value: string, field: string) => {
    if (!value || value === "") {
      return field === "AuthorName"
        ? "Author Name is required"
        : "File title is required";
    }
    if (!patterns.quizNameLengthRegex.test(value)) {
      return messages.quizNameLength;
    }
    if (!patterns.fileNameLengthRegex.test(value)) {
      return field === "AuthorName"
        ? messages.authorNameLength
        : messages.fileNameLength;
    }
    if (!patterns.fileNameRegex.test(value)) {
      return field === "AuthorName" ? messages.authorName : messages.fileName;
    }
    if (patterns.consecutiveSpecialCharsRegex.test(value)) {
      return field === "AuthorName"
        ? messages.authorNameConsecutiveSpecialChars
        : messages.consecutiveSpecialChars;
    }
    if (!patterns.filenameAtleast1Alpha.test(value)) {
      return field === "AuthorName"
        ? messages.authorNameAtlease1Alpha
        : messages.filenameAtlease1Alpha;
    }
    return null;
  };

  const handleAuthorNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAuthorNameValue(value);
    setAuthorNameError("");
    if (hasSubmitted) {
      const validationError = validateFileName(value, "AuthorName");
      setAuthorNameError(validationError);
    }
  };


  return (
    <div className="admin-bulk-upload">
      <div className="title-div">
        <div className="arrow-image">
          <img
            src={backArrow}
            alt="backButton"
            className="adminsettings-backbutton"
            onClick={() => navigate("/admin-materials-upload")}
          />
        </div>
        <TabsContentHeader
          title="Upload"
          subtitle="Bulk Upload"
          addLabel="Bulk Upload"
          subImage={bookIcon}
          searchValue=""
          setSearchValue={() => {}}
        />
      </div>
      <div className="admin-bulk-upload-container">
        <div className="subject-path">
          <div className="subject-div">
            <div className="subject-title">Select Subject/Course</div>
            <Select
              onChange={(option) => setSelectedSubject(option)}
              value={selectedSubject}
              placeholder="Select Subject"
              classNamePrefix="dropdown-indicator"
              styles={customStyle}
              options={subjectOptions}
              isLoading={loading}
            />
          </div>
          <div className="subject-div">
            <div className="subject-title">Select Path</div>
            <Select
              onChange={(option) =>
                setSelectedPath(option as PathOption | null)
              }
              value={selectedPath}
              placeholder="Select Path"
              classNamePrefix="dropdown-indicator"
              styles={customStyle}
              options={pathoptions as PathOption[]}
              isLoading={loading}
            />
          </div>
        </div>

        {selectedPath?.value === "Syndicated Videos" && (
          <div className="subject-div">
            <div className="subject-title">Video Category</div>
            <Autocomplete
              // freeSolo
              options={
                categories.length > 0
                  ? categories
                  : [{ label: "No options", id: "no-options" }]
              }
              getOptionLabel={(option) => {
                // Value selected with enter, right from the input
                if (typeof option === "string") {
                  return option;
                }
                // Add "xxx" option created dynamically
                if (option.inputValue) {
                  return option.inputValue;
                }
                // Regular option
                return option.label;
              }}
              loading={getCategoryLoading || addCategoryLoading}
              sx={sxStyles}
              componentsProps={{
                popper: {
                  sx: {
                    "& .MuiAutocomplete-paper .MuiAutocomplete-listbox": {
                      padding: 0,
                      overflowX: "scroll",
                    },
                    "& .MuiAutocomplete-paper .MuiAutocomplete-listbox .MuiAutocomplete-option":
                      {
                        whiteSpace: "nowrap",
                        minWidth: "fit-content",
                      },
                  },
                },
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                setVideoCategoryError("");
                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = options.some(
                  (option) =>
                    inputValue?.toLowerCase() === option?.label?.toLowerCase()
                );
                if (inputValue !== "" && !isExisting) {
                  filtered.push({
                    inputValue,
                    id: "add-new",
                    label: `Add "${inputValue}"`,
                  });
                }

                return filtered;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Video Category"
                  variant="outlined"
                  onChange={() => {
                    setVideoCategoryError("");
                  }}
                  onInput={() => {
                    setVideoCategoryError("");
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {getCategoryLoading || addCategoryLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              onInputChange={(event, value) => {
                setInputValue(value);
                if (setVideoCategoryData) {
                  setVideoCategoryData(null);
                }
                const validationError = validateVideoCategory(value);

                setVideoCategoryError(validationError);
              }}
              onChange={async (event, newValue) => {
                setVideoCategoryError("");
                setVideoCategoryValue(null);
                if (typeof newValue === "string") {
                  // const inputValue = newValue;
                  // const validationError = validateVideoCategory(inputValue);
                  // if (!validationError) {
                  //   const response = await addCategory({
                  //     variables: { category: inputValue?.trim(), sjId: selectedSubjectId }
                  //   });
                  //   const addedCategoryId = response.data.addSubjectCategory;
                  //   setVideoCategoryError('');
                  //   setVideoCategoryValue({ id: addedCategoryId, label: inputValue });
                  //   getCategory({ variables: { sjId: selectedSubjectId } });
                  // } else {
                  //   setVideoCategoryValue({ id: undefined, label: inputValue });
                  //   setVideoCategoryError(validationError);
                  // }
                } else if (newValue?.inputValue) {
                  const inputValue = newValue?.inputValue;
                  const validationError = validateVideoCategory(inputValue);
                  if (!validationError) {
                    const response = await addCategory({
                      variables: {
                        category: inputValue?.trim(),
                        sjId: selectedSubject?.value,
                      },
                    });
                    const addedCategoryId = response.data.addSubjectCategory;
                    setVideoCategoryError("");
                    setVideoCategoryValue({
                      id: addedCategoryId,
                      label: inputValue,
                    });
                    getCategory({
                      variables: { sjId: selectedSubject?.value },
                    });
                  } else {
                    setVideoCategoryValue({ id: undefined, label: inputValue });
                    setVideoCategoryError(validationError);
                  }
                } else if (!newValue) {
                  setVideoCategoryError("Video category is required");
                } else {
                  const validationError = validateVideoCategory(newValue.label);
                  if (validationError) {
                    setVideoCategoryError(validationError);
                  }
                  setVideoCategoryValue(newValue);
                }
              }}
              value={videoCategoryValue}
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{
                    opacity:
                      categories.length < 1 && inputValue === "" ? 0.5 : 1,
                  }}
                >
                  {typeof option === "string" ? option : option.label}
                </li>
              )}
              getOptionDisabled={(option) => {
                // Disable options if there are no categories
                if (categories.length < 1 && option?.id !== "add-new") {
                  return typeof option !== "string"; // Disable non-string options
                }
                return false; // Enable all options when there are categories
              }}
            />
            {hasSubmitted === true && videoCategoryError && (
              <div className="errorMessage">{videoCategoryError}</div>
            )}
          </div>
        )}

        {selectedPath?.value === "Podcast" && (
          <div className="subject-div">
            <div className="subject-title">Description</div>
            <div className="inputContainer">
              <textarea
                className="uploadedFileTextarea"
                data-testid="uploadedFile-textarea"
                placeholder="Messages goes here..."
                value={descriptionValue}
                onChange={handleDescriptionChange}
                ref={descriptionRef} // Add ref here
              />
              {hasSubmitted && descriptionError && (
                <div className="errorMessage">{descriptionError}</div>
              )}
            </div>
          </div>
        )}

        {selectedPath?.value === "E-books (Examination Guide)" && (
          <div className="subject-div">
            <div className="subject-title">Author Name</div>
            <div className="inputContainer">
              <input
                className="uploadedFile"
                data-testid="uploadedFile"
                placeholder="Name"
                value={authorNameValue}
                onChange={handleAuthorNameChange}
              />
              {hasSubmitted === true && authorNameError && (
                <div className="errorMessage">{authorNameError}</div>
              )}
            </div>
          </div>
        )}

        <div className="class-grade">
          <div className="class-grade-title">Add Class/Grade</div>
          <ClassroomSelector
            allClasses={allClasses}
            subjectClasses={selectedClasses}
            setSelectedClassIds={setSelectedClassIds}
            selectedClassIds={selectedClassIds}
          />
        </div>
        <div className="bulk-upload-main">
          <div className="bulk-upload-title">Upload</div>
          <BulkUpload
            selectedPath={selectedPath?.label!}
            selectedClassIds={selectedClassIds}
            selectedSubject={selectedSubject?.value!}
            setHasSubmitted={setHasSubmitted}
            ErrorInFields={ErrorInFields!}
            videoCategoryId = {videoCategoryValue?.id}
            descriptionValue = {descriptionValue}
            authorName = {authorNameValue}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminBulkUpload;
